/** Dependency-free inline charts for the admin dashboards (no chart library —
 *  matches the locked stack + the existing weekly sparkline). */

export type ChartTone = 'emerald' | 'sky' | 'amber' | 'rose' | 'neutral';
const TONE: Record<ChartTone, { bar: string; text: string }> = {
  emerald: { bar: 'bg-emerald-500/80 hover:bg-emerald-600', text: 'text-emerald-600' },
  sky: { bar: 'bg-sky-500/80 hover:bg-sky-600', text: 'text-sky-600' },
  amber: { bar: 'bg-amber-500/80 hover:bg-amber-600', text: 'text-amber-600' },
  rose: { bar: 'bg-rose-500/80 hover:bg-rose-600', text: 'text-rose-600' },
  neutral: { bar: 'bg-neutral-400/80 hover:bg-neutral-500', text: 'text-neutral-600' },
};

export interface TrendPoint {
  label: string;
  value: number;
}

/** A compact weekly trend: headline (latest + vs-last delta), a 12-bar column
 *  chart, and the peak. Used for active-accounts and statement-volume usage. */
export function WeeklyTrend({
  title,
  data,
  tone = 'emerald',
  caption,
}: {
  title: string;
  data: TrendPoint[];
  tone?: ChartTone;
  caption?: string;
}) {
  if (data.length === 0) return null;
  const t = TONE[tone];
  const max = Math.max(1, ...data.map((d) => d.value));
  const latest = data[data.length - 1]?.value ?? 0;
  const prev = data[data.length - 2]?.value ?? 0;
  const delta = latest - prev;
  const peak = Math.max(...data.map((d) => d.value), 0);
  return (
    <div className="rounded-xl border border-neutral-200/70 bg-white p-4">
      <div className="flex items-end gap-5">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
            {title}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-semibold tabular-nums">{latest.toLocaleString()}</span>
            {data.length > 1 && (
              <span
                className={`text-xs font-medium ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-rose-600' : 'text-neutral-400'}`}
              >
                {delta > 0 ? '▲' : delta < 0 ? '▼' : '–'} {Math.abs(delta)} vs last wk
              </span>
            )}
          </div>
        </div>
        <div className="flex h-12 flex-1 items-end justify-end gap-1" aria-label={`${title} trend`}>
          {data.map((d) => (
            <div
              key={d.label}
              title={`Week of ${d.label}: ${d.value.toLocaleString()}`}
              className={`w-3 rounded-sm transition ${t.bar}`}
              style={{ height: `${Math.max(2, Math.round((d.value / max) * 48))}px` }}
            />
          ))}
        </div>
        <div className="text-xs text-neutral-400">
          peak {peak.toLocaleString()} · {data.length} wks
        </div>
      </div>
      {caption && <p className="mt-2 text-xs text-neutral-400">{caption}</p>}
    </div>
  );
}

export interface HBar {
  label: string;
  value: number;
  tone?: ChartTone;
}

/** Horizontal labeled bars for a small breakdown (e.g. accounts by issue type). */
export function HBars({ data, empty }: { data: HBar[]; empty?: string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const shown = data.filter((d) => d.value > 0);
  if (shown.length === 0)
    return <p className="text-sm text-neutral-400">{empty ?? 'Nothing to show.'}</p>;
  return (
    <div className="space-y-2">
      {shown.map((d) => {
        const t = TONE[d.tone ?? 'neutral'] ?? TONE.neutral;
        return (
          <div key={d.label} className="flex items-center gap-3 text-sm">
            <div className="w-40 shrink-0 truncate text-neutral-600" title={d.label}>
              {d.label}
            </div>
            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-neutral-100">
              <div
                className={`h-full rounded-full ${t.bar}`}
                style={{ width: `${Math.round((d.value / max) * 100)}%` }}
              />
            </div>
            <div className={`w-8 shrink-0 text-right tabular-nums font-medium ${t.text}`}>
              {d.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}
